import type { AutomationAppUIModel } from '@/types/automation';
import { formatDateTime } from '@/utils/dateTime';
import { formatUserDisplayName } from '@/utils/primitives';
import { DotStatusBadge, type DotStatusKind } from '@/components/shared/DataTable';
import { EntityOverviewHeader, type EntityOverviewHeaderProps } from './EntityOverviewHeader';

export interface AppOverviewHeaderProps
  extends Pick<EntityOverviewHeaderProps, 'onMoreActions' | 'onBack' | 'backLabel'> {
  /**
   * The app resource. May be undefined while the app query is still resolving —
   * fields render as "—" placeholders in that case.
   */
  app: AutomationAppUIModel | null | undefined;
  /**
   * The app name to display as the title. Accepted separately from `app.name`
   * because the URL param is reliable even before the app query resolves.
   */
  name: string;
  /**
   * Region/location of the app. Sourced from the URL param by callers rather
   * than `app.location` so it's stable across loading states.
   */
  region: string | undefined;
  /** Number of workflows in the app. */
  workflowCount: number;
}

/**
 * Maps an app's ARM `provisioningState` to the dot-status used in the header.
 *
 * The header has its own mapping (separate from the table row in AppListPage)
 * because a successfully provisioned app without an `appResourceId` is shown
 * as Inactive here — the row uses a different status taxonomy.
 */
export const getAppDotStatus = (app: AutomationAppUIModel | null | undefined): DotStatusKind => {
  const provisioning = app?.properties?.provisioningState?.toLowerCase();
  if (provisioning === 'failed' || provisioning === 'canceled') return 'Error';
  if (provisioning === 'deleting' || provisioning === 'deleted') return 'Deleting';
  if ((!provisioning || provisioning === 'succeeded') && app?.properties?.appResourceId) {
    return 'Active';
  }
  if (!provisioning) return 'Inactive';
  return 'Provisioning';
};

/**
 * Page-level header for app-scoped pages (e.g., the workflows list).
 *
 * Thin domain-aware wrapper around `EntityOverviewHeader` that knows how to
 * extract and format the standard app metadata row (region · workflows count ·
 * owner · last modified · status). Owns the provisioning→DotStatus mapping
 * for the header so callers don't have to repeat it.
 */
export const AppOverviewHeader = ({
  app,
  name,
  region,
  workflowCount,
  onMoreActions,
  onBack,
  backLabel,
}: AppOverviewHeaderProps) => (
  <EntityOverviewHeader
    name={name}
    kindLabel="App"
    moreActionsLabel="More app actions"
    metadataAriaLabel="App metadata"
    {...(onMoreActions ? { onMoreActions } : {})}
    {...(onBack ? { onBack } : {})}
    {...(backLabel ? { backLabel } : {})}
    metadata={[
      <>Region: {region ?? '—'}</>,
      <>Workflows: {workflowCount}</>,
      <>Owner: {formatUserDisplayName(app?.properties?.createdBy)}</>,
      <>
        Last modified:{' '}
        {app?.properties?.updatedOn ? formatDateTime(app.properties.updatedOn, 'full') : '—'}
      </>,
      <>
        Status: <DotStatusBadge status={getAppDotStatus(app)} />
      </>,
    ]}
  />
);
